import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  ScanText,
  Trash2,
  ChevronDown,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  File,
  PlayCircle,
} from "lucide-react";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { AvailableVariable } from "../../common/ui/types";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import NodeDocsButton from "../../docs/NodeDocsButton";



import syntaxHighlight from "../../utils/jsonViewer";

export const OcrProcessingNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [processingType, setProcessingType] = useState<string>(
    data?.file_processing_type || data?.processing_type || ""
  );
  const [fileKey, setFileKey] = useState<string>(
    data?.files || data?.key || ""
  );
  const [prompt, setPrompt] = useState<string>(data?.prompt || "");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setDraggedVariable] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { setNodes, deleteElements } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();

  const hasOutputs = data?.outputs && Object.keys(data.outputs).length > 0;

  // Execution status - match summarizeNode pattern
  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const currentNode = useMemo(() => {
    return nodes.find((n) => n.id === id);
  }, [nodes, id]);

  const validationStatus = useMemo(() => {
    if (!currentNode) return { isValid: true, errors: [] };
    return getNodeValidationStatus(currentNode, edges, nodes);
  }, [currentNode, edges, nodes]);

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => {
      e.stopPropagation();
    },
    []
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object")
      return { inputs: {}, outputs: {} };

    const inputs: Record<string, any> = {};
    let outputs: any = {};

    // Check if outputs property exists and is an array (OneDrive connector case)
    if (nodeData.outputs && Array.isArray(nodeData.outputs)) {
      outputs = nodeData.outputs; // Keep it as array

      // Extract inputs from nodeData
      Object.keys(nodeData).forEach((key) => {
        if (
          key === "outputs" ||
          key.startsWith("_") ||
          key === "id" ||
          key === "executionStatus"
        )
          return;
        const value = nodeData[key];
        inputs[key] = value;
      });
    } else if (nodeData.outputs && typeof nodeData.outputs === "object") {
      // Outputs exist as an object
      outputs = nodeData.outputs;

      // Extract inputs from nodeData
      Object.keys(nodeData).forEach((key) => {
        if (
          key === "outputs" ||
          key.startsWith("_") ||
          key === "id" ||
          key === "executionStatus"
        )
          return;
        const value = nodeData[key];
        inputs[key] = value;
      });
    } else {
      // Legacy: Standard key-value outputs mixed in nodeData
      Object.keys(nodeData).forEach((key) => {
        if (key.startsWith("_") || key === "id") return;
        const value = nodeData[key];
        if (typeof value === "string" && value.includes(".outputs.")) {
          inputs[key] = value;
        } else {
          outputs[key] = value;
        }
      });
    }

    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const ancestorNodes = getAllAncestorNodes(id, nodes, edges);

    return ancestorNodes.map((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      return {
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      };
    }) as AvailableVariable[];
  }, [id, nodes, edges, extractNodeData]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output"
  ) => {
    e.stopPropagation();
    // Manual formatting to ensure no {{ }} braces are added for OCR payload
    const fullVariable =
      type === "output"
        ? variable
          ? `${nodeName}.outputs.${variable}`
          : `${nodeName}.outputs`
        : variable
        ? `${nodeName}.inputs.${variable}`
        : `${nodeName}.inputs`;

    setDraggedVariable(fullVariable);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };



  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable) {
      setFileKey(variable);
    }
    setDraggedVariable(null);
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const statusConfig = useMemo(() => {
    if (isExecuting) {
      return { bg: "bg-blue-500", text: "Executing...", pulse: true };
    } else if (executionSuccess) {
      return {
        bg: "bg-green-500",
        text: "Executed Successfully",
        pulse: false,
      };
    } else if (executionError) {
      return { bg: "bg-red-500", text: "Execution Failed", pulse: false };
    }

    const hasConfig = processingType && fileKey;
    return hasConfig
      ? { bg: "bg-violet-500", text: "Configured", pulse: false }
      : { bg: "bg-gray-400", text: "Not Configured", pulse: false };
  }, [processingType, fileKey, isExecuting, executionSuccess, executionError]);

  // Auto-populate fileKey when OneDrive Connector node is connected
  // Auto-populate fileKey when OneDrive Connector or Document Store node is connected
  useEffect(() => {
    // Only auto-populate if fileKey is empty
    if (fileKey) return;

    const multiFileNode = previousNodes.find(
      (node) =>
        node.nodeType === "ONEDRIVE-CONNECTOR" ||
        node.nodeType === "DOCUMENT-STORE"
    );

    if (multiFileNode) {
      let autoFileKey = "";
      if (multiFileNode.nodeType === "ONEDRIVE-CONNECTOR") {
        // OneDrive connector outputs an array, so just reference the outputs array
        autoFileKey = `${multiFileNode.nodeName}.outputs`;
      } else if (multiFileNode.nodeType === "DOCUMENT-STORE") {
        // Document Store outputs file_ids array
        autoFileKey = `${multiFileNode.nodeName}.outputs.file_ids`;
      }

      if (autoFileKey) {
        console.log(
          "🔗 Auto-populating OCR fileKey from previous node:",
          autoFileKey
        );
        setFileKey(autoFileKey);
      }
    }
  }, [previousNodes, fileKey]);

  // Sync node data when config changes (but NOT during execution)
  useEffect(() => {
    // Skip updating during execution to avoid overwriting executionStatus
    if (isExecuting) return;

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;

        // Use 'files' key if:
        // 1. The key references any outputs (contains .outputs)
        // 2. The key references a file_ids array
        const shouldUseFilesKey =
          (fileKey && fileKey.includes(".outputs")) ||
          (fileKey && fileKey.includes(".file_ids"));

        const updatedData: any = {
          ...node.data,
          file_processing_type: processingType,
          prompt,
        };

        if (shouldUseFilesKey && fileKey) {
          updatedData.files = fileKey;
          delete updatedData.key;
        } else {
          updatedData.key = fileKey;
          delete updatedData.files;
        }

        return {
          ...node,
          data: updatedData,
        };
      })
    );
  }, [processingType, fileKey, prompt, id, setNodes, isExecuting]);


  return (
    <div
      className={`relative group bg-violet-50 border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        !validationStatus.isValid
          ? "border-red-500 shadow-red-200"
          : isExecuting
          ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
          : executionSuccess
          ? "border-green-500 shadow-md shadow-green-200"
          : executionError
          ? "border-red-500 shadow-md shadow-red-200"
          : selected
          ? "border-violet-500 shadow-md"
          : "border-violet-300 hover:border-violet-300"
      }`}
    >
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}
      {executionSuccess && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
      {executionError && !isExecuting && (
         <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Execute Button */}
      <div className="absolute -top-3 -right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onExecuteNode?.(id);
          }}
          className="p-1.5 bg-blue-500 text-white rounded-full shadow-md hover:bg-blue-600 transition-all"
          title="Execute to this node"
        >
          {isExecuting ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <PlayCircle className="w-3 h-3" />
          )}
        </button>
      </div>

      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-violet-600 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(154, 8, 252, 0.2), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg shadow-sm bg-violet-100">
            <ScanText className="w-4 h-4 text-violet-700" />
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">
              OCR Processing
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                  statusConfig.pulse ? "animate-pulse" : ""
                }`}
              />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="FILE-OCR" />
          <button
            onClick={handleDeleteClick}
            className="text-gray-500 hover:text-gray-700 p-1 rounded transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!validationStatus.isValid && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-900 mb-1">
                Configuration Required
              </p>
              <ul className="text-red-700 space-y-0.5 list-disc list-inside">
                {validationStatus.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="mb-3" onClick={stopPropagation}>
        <button
          onClick={handleOpenModal}
          className="w-full py-2.5 rounded-lg bg-violet-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-violet-700"
        >
          <Settings className="w-3.5 h-3.5" /> Configure OCR
        </button>
      </div>

      {(fileKey || processingType || hasOutputs) && (
        <div
          className="p-2.5 bg-white/80 border border-violet-200 rounded-lg text-xs space-y-1.5 shadow-inner"
          onClick={stopPropagation}
        >
          {processingType && (
            <div className="flex items-start gap-1.5">
              <span className="text-gray-500 shrink-0">Type:</span>
              <span className="text-violet-700 bg-violet-100 px-2 py-0.5 rounded text-[11px] font-medium">
                {processingType === "AI_OCR" ? "AI OCR" : "Normal OCR"}
              </span>
            </div>
          )}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-violet-600 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(154, 8, 252, 0.2), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />

      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
            style={{ margin: 0 }}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-50 rounded">
                    <ScanText className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure OCR Processing
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Set up your OCR processing parameters
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>
                <div className="flex-1 overflow-y-auto bg-white">
                  <div className="p-6">
                    {!validationStatus.isValid && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-red-900 mb-2">
                              Configuration Issues
                            </h4>
                            <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                              {validationStatus.errors.map((error, idx) => (
                                <li key={idx}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      OCR Configuration
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                      Configure OCR processing parameters
                    </p>

                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          File to Process{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Drag a file output from a previous node into the box
                          below.
                        </p>
                        <div
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`min-h-[120px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all flex items-center justify-center ${
                            isDraggingOver
                              ? "border-violet-500 bg-violet-50 shadow-inner"
                              : fileKey
                              ? "border-green-400 bg-green-50"
                              : "border-gray-300 bg-gray-50 hover:border-violet-400"
                          }`}
                        >
                          {fileKey ? (
                            <div className="flex items-center justify-between gap-3 w-full">
                              <div className="flex items-center gap-3">
                                <File className="w-6 h-6 text-green-600" />
                                <div>
                                  <div className="text-xs font-medium text-gray-500">
                                    File Selected
                                  </div>
                                  <code className="text-xs text-green-800 font-mono break-all">
                                    {fileKey}
                                  </code>
                                </div>
                              </div>
                              <button
                                onClick={() => setFileKey("")}
                                className="text-gray-400 hover:text-red-600 p-1 hover:bg-white rounded-full transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-center text-gray-500">
                              <File className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                              <span className="text-sm font-medium">
                                Drop File Here
                              </span>
                              <p className="text-xs">
                                This input is required for OCR processing.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Processing Type{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Choose the OCR processing method
                        </p>
                        <div className="relative">
                          <select
                            value={processingType}
                            onChange={(e) => setProcessingType(e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-violet-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition appearance-none cursor-pointer"
                          >
                            <option value="">Select processing type...</option>
                            <option value="NORMAL_OCR">Normal OCR</option>
                            <option value="AI_OCR">AI OCR</option>
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        {processingType && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-800">
                              <strong>
                                {processingType === "AI_OCR"
                                  ? "AI OCR:"
                                  : "Normal OCR:"}
                              </strong>{" "}
                              {processingType === "AI_OCR"
                                ? "Advanced AI-powered optical character recognition with higher accuracy"
                                : "Standard optical character recognition for basic text extraction"}
                            </p>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prompt <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <textarea
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          placeholder="Enter specific instructions or context for the OCR processing..."
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-violet-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition resize-y min-h-[100px]"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Provide additional context or instructions to guide the OCR model (especially for AI OCR).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-80 bg-gray-50 overflow-y-auto">
                  <div className="p-4">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Node Outputs
                      </h3>
                      <p className="text-xs text-gray-500">
                        Available after execution
                      </p>
                    </div>

                    {hasOutputs ? (
                      <div className="space-y-3">
                        {Object.entries(data.outputs).map(
                          ([key, value]: [string, any]) => (
                            <div
                              key={key}
                              className="bg-white border border-gray-200 rounded-lg p-3 hover:border-green-300 transition-all"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {key}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-mono">
                                  {typeof value}
                                </span>
                              </div>
                              <pre
                                className="text-xs font-mono break-all whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{
                                  __html: syntaxHighlight(value),
                                }}
                              />
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
                        <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                          <ScanText className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-600 font-medium mb-1">
                          No outputs yet
                        </p>
                        <p className="text-xs text-gray-500">
                          Run the workflow to generate outputs
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {validationStatus.isValid ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-red-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        {validationStatus.errors.length} issue(s) found
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCloseModal}
                    disabled={!fileKey || !processingType}
                    className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-violet-600"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OcrProcessingNode;
